import React, { useCallback, useEffect, useState } from 'react';

import { FileText } from 'lucide-react';

import { loadMarkdownAttachment } from '../../lib/markdownAttachment';
import { MediaAttachmentStates, getUploadFile, isUploadInflightState } from '../../lib/media_attachments_store';
import { cn } from '../../lib/utils';

import SpinnerIcon from '../../../assets/images/spinner.svg';

import { KEYCHAIN } from '../../contexts/KeyMaster';
import MarkdownAttachmentModal from '../modals/MarkdownAttachmentModal';
import { ModalPortal } from '../modals/ModalPortal';

const MarkdownAttachment = ({
  compact = false,
  downloadUrl = null,
  fileName,
  renderMetadata,
  showUploadStatus = true,
  size = 'h-80 w-60',
  source,
  uploadError = null,
  uploadProgress = 0,
  uploadState = null,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [preview, setPreview] = useState({ error: null, markdown: '', status: 'idle' });

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const abortController = new AbortController();
    setPreview({ error: null, markdown: '', status: 'loading' });

    loadMarkdownAttachment(source, { loadLocalUpload: getUploadFile, signal: abortController.signal })
      .then((markdown) => {
        setPreview({ error: null, markdown, status: 'loaded' });
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setPreview({ error: error?.message || 'The Markdown preview could not be loaded.', markdown: '', status: 'error' });
      });

    return () => {
      abortController.abort();
    };
  }, [isModalOpen, source]);

  const handleOpen = useCallback(() => {
    setPreview({ error: null, markdown: '', status: 'loading' });
    setIsModalOpen(true);
  }, []);
  const handleClose = useCallback(() => setIsModalOpen(false), []);
  const metadata = typeof renderMetadata === 'function' ? renderMetadata() : null;
  const isUploading = isUploadInflightState(uploadState);
  const isProcessing = uploadState === MediaAttachmentStates.processing;
  const isRetrying = uploadState === MediaAttachmentStates.retrying;
  const isError = uploadState === MediaAttachmentStates.error;
  const progress = Number.isFinite(uploadProgress) ? Math.min(100, Math.max(0, Math.round(uploadProgress))) : 0;

  return (
    <>
      <div
        className={cn(
          'group/markdown relative shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm',
          size,
          isRetrying && 'outline outline-1 outline-yellow-400',
        )}
      >
        <button
          type="button"
          onClick={handleOpen}
          className={cn('block size-full cursor-pointer overflow-hidden text-left', compact ? 'p-3' : 'p-5')}
          aria-label={`Preview ${fileName || 'Markdown attachment'}`}
        >
          <div className={cn('flex size-full flex-col items-center justify-center text-center text-gray-500', compact ? 'gap-2' : 'gap-4')}>
            <FileText size={compact ? 44 : 64} strokeWidth={1.25} aria-hidden="true" />
            <span className="font-mono text-2xs font-semibold tracking-wide lowercase">markdown</span>
          </div>
        </button>
        {showUploadStatus && (isUploading || isRetrying) && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-black/50 text-xs font-semibold text-white">
            {progress}%
          </div>
        )}
        {showUploadStatus && isProcessing && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-black/50 text-white">
            <SpinnerIcon className="size-4 animate-spin" />
          </div>
        )}
        {showUploadStatus && isError && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-black/50 px-2 text-center text-2xs leading-tight text-white">
            {uploadError || 'Error'}
          </div>
        )}
        {metadata && (
          <span className="absolute top-2 left-2 max-w-[calc(100%-1rem)] rounded-full bg-black/60 px-2 py-1 font-mono text-white opacity-0 shadow-[inset_0_0_0_0.5px_rgba(255,255,255,0.2)] backdrop-blur-[15px] backdrop-saturate-200 transition-opacity group-hover/markdown:opacity-100 has-[a:hover]:bg-black/40">
            {metadata}
          </span>
        )}
      </div>

      <ModalPortal open={isModalOpen} onClose={handleClose} size="document" keychainId={KEYCHAIN.MODAL__MARKDOWN_ATTACHMENT}>
        <MarkdownAttachmentModal
          error={preview.error}
          fileName={fileName}
          markdown={preview.markdown}
          onClose={handleClose}
          downloadUrl={downloadUrl}
          status={preview.status}
        />
      </ModalPortal>
    </>
  );
};

export default MarkdownAttachment;
